import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WebsiteLayout from '@/components/common/Layouts/WebsiteLayout';
import ApplyNow from '@/components/common/ApplyNow/ApplyNow';
import EnrollModal from '@/components/common/Modal/EnrollModal';
import DirectorsDesk from './components/DirectorsDesk';
import galleryApi from '@/services/api/galleryApi';
import { useSEO } from '@/hooks/useSEO';
import { INSTITUTION_INFO } from '@/config/constants';

const About = () => {
  // SEO Management
  const { seoData, loading: seoLoading } = useSEO('about-us', 'en');
  
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [missionImage, setMissionImage] = useState(null);
  const [galleryData, setGalleryData] = useState([]);
  const [facilityData, setFacilityData] = useState([]);

  useEffect(() => {
    const loadGalleryImages = async () => {
      try {
        const response = await galleryApi.getPublicGalleryByPageSlug('about-us');
        if (response.data && response.data.length > 0) {
          setGalleryData(response.data);
          
          // Get mission section image by specific slug
          const missionImageItem = response.data.find(item => item.slug === 'about-us-our-mission-image-dri-nqe');
          if (missionImageItem) {
            setMissionImage(missionImageItem.media_url);
          }

          // Get facility images by specific slugs
          const facilitySlugs = [
            'about-us-training-facility-one-cez-ng4',
            'about-us-training-facility-two-u0i-bih',
            'about-us-training-facility-thr-ctl-f0b'
          ];

          const defaultFacilities = [
            {
              image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              title: '50-Acre Training Yard',
              description: 'Spacious outdoor training area with various terrain types to simulate real working conditions.'
            },
            {
              image: 'https://images.unsplash.com/photo-1562813733-b31f71025d54?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              title: 'Modern Classrooms',
              description: 'State-of-the-art classrooms equipped with multimedia technology for effective learning.'
            },
            {
              image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              title: 'Latest Equipment Fleet',
              description: 'Comprehensive collection of modern heavy equipment for hands-on training experience.'
            }
          ];

          const facilities = facilitySlugs.map((slug, index) => {
            const facilityItem = response.data.find(item => item.slug === slug);
            if (facilityItem) {
              return {
                image: facilityItem.media_url,
                title: facilityItem.title && facilityItem.title.trim() !== '' 
                  ? facilityItem.title 
                  : defaultFacilities[index].title,
                description: facilityItem.caption && facilityItem.caption.trim() !== '' 
                  ? facilityItem.caption 
                  : defaultFacilities[index].description
              };
            }
            return defaultFacilities[index];
          });

          setFacilityData(facilities);
        }
      } catch (error) {
        console.error('Error loading gallery images:', error);
      }
    };

    loadGalleryImages();
  }, []);

  const stats = [
    { number: INSTITUTION_INFO.studentsTrained, label: 'Students Trained' },
    { number: INSTITUTION_INFO.placementRate, label: 'Job Placement Rate' },
    { number: INSTITUTION_INFO.experience, label: 'Years of Experience' },
    { number: INSTITUTION_INFO.partners, label: 'Industry Partners' }
  ];

  const values = [
    {
      title: 'Safety First',
      description: 'Safety is our top priority in every aspect of training. We maintain the highest safety standards and instill a safety-first mindset in all our students.',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
    },
    {
      title: 'Excellence in Training',
      description: 'We provide the highest quality training programs using state-of-the-art equipment and industry-proven methods to ensure our graduates are job-ready.',
      icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
    },
    {
      title: 'Career Support',
      description: 'Our commitment extends beyond training. We provide ongoing career support, job placement assistance, and industry connections to help our graduates succeed.',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z'
    },
    {
      title: 'Industry Leadership',
      description: 'With decades of experience and strong industry partnerships, we stay at the forefront of heavy equipment training and construction industry trends.',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
    }
  ];



  const timeline = [
    {
      year: INSTITUTION_INFO.establishedYear.toString(),
      title: 'Founded',
      description: `${INSTITUTION_INFO.name} was established with a vision to provide quality heavy equipment training.`
    },
    {
      year: '2019',
      title: 'First Batch Success',
      description: 'Successfully trained and placed our first batch of students with 100% placement rate.'
    },
    {
      year: '2020',
      title: 'Facility Expansion',
      description: 'Expanded our training facility with modern equipment and enhanced safety protocols.'
    },
    {
      year: '2021',
      title: 'Industry Recognition',
      description: 'Gained recognition from major construction companies for quality training standards.'
    },
    {
      year: '2022',
      title: '1000+ Students',
      description: 'Celebrated training over 1000 students with excellent placement records.'
    },
    {
      year: '2024',
      title: 'Continued Growth',
      description: `Today, we have trained ${INSTITUTION_INFO.studentsTrained} students and maintain partnerships with ${INSTITUTION_INFO.partners} companies.`
    }
  ];

  return (
    <WebsiteLayout className="pt-16 lg:pt-20">
      <ApplyNow />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-orange-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Heavy equipment training facility"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            About {INSTITUTION_INFO.name}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
            Since {INSTITUTION_INFO.establishedYear}, we've been transforming careers through comprehensive heavy equipment training programs, preparing skilled operators for success in the construction and mining industries.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-orange-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 dark:text-gray-400 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                To provide the highest quality heavy equipment training in a safe, supportive environment while preparing our students for successful careers in the construction and mining industries.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                We believe that proper training not only creates skilled operators but also builds careers, strengthens families, and contributes to the growth of our communities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/courses"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-bold text-lg rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  View Our Courses
                  <svg className="ml-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 border-2 border-orange-500 text-orange-500 font-semibold text-lg rounded-lg hover:bg-orange-500 hover:text-white transition-all duration-300"
                >
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src={missionImage || "https://images.unsplash.com/photo-1590845947670-c009801ffa74?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                alt="Training facility"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Director's Desk Section */}
      <DirectorsDesk galleryData={galleryData} />

      {/* Values Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              These principles guide everything we do and shape the experience of every student who trains with us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={value.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              From humble beginnings to industry leadership, here's how we've grown over the years.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-0.5 top-0 bottom-0 w-0.5 bg-orange-200 dark:bg-orange-800"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline Dot */}
                  <div className="absolute left-4 md:left-1/2 transform -translate-x-1/2 w-4 h-4 bg-orange-500 border-4 border-white dark:border-gray-900 rounded-full z-10"></div>

                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <div className="text-2xl font-bold text-orange-500 mb-2">
                        {item.year}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              World-Class Training Facilities
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Our 50-acre training facility features the latest equipment and technology to provide hands-on, real-world training experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {facilityData.map((facility, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <img
                  src={facility.image}
                  alt={facility.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {facility.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {facility.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Ready to Start Your Career Journey?
          </h2>
          <p className="text-lg text-gray-800 mb-8">
            Join thousands of successful graduates who've built rewarding careers through our comprehensive training programs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setEnrollModalOpen(true)}
              className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-bold text-lg rounded-lg hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Enroll Now
              <svg className="ml-2 w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <Link
              to="/gallery/images"
              className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-gray-900 text-gray-900 font-semibold text-lg rounded-lg hover:bg-gray-900 hover:text-white transition-all duration-300"
            >
              View Our Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      <EnrollModal
        isOpen={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
      />
    </WebsiteLayout>
  );
};

export default About;
